import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function Upgrade() {
  const { user, token, isImpersonating } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleUpgradeRequest = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Calls your existing billing.js route
      await axios.post(`${baseURL}/api/billing/request-upgrade`, {
        orgId: user.org_id,
        targetTier: 1
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      setSuccessMessage('Your upgrade request has been submitted successfully! Your account administrator will review and unlock Tier 1 access shortly.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit upgrade request. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-slate-900">Unlock Enterprise AI Governance</h2>
        <p className="text-slate-500 mt-2 text-lg">Upgrade to Tier 1 to expand your inventory capacity and generate comprehensive board reports.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl mb-6 font-medium shadow-sm flex items-center gap-3">
          <span className="text-xl">⚠️</span> {error}
        </div>
      )}

      {successMessage ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center shadow-sm">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-2xl font-bold text-emerald-900 mb-2">Request Received</h3>
          <p className="text-emerald-700 font-medium max-w-md mx-auto mb-6">{successMessage}</p>
          
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Current Tier (Free Trial) */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm opacity-70">
            <div className="inline-block bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">Current Plan</div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Free Trial</h3>
            <p className="text-slate-500 text-sm mb-6">Basic governance exploration limited to 7 days.</p>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-3 text-slate-600 font-medium">
                <span className="text-slate-400">✓</span> Up to 3 AI Tools
              </li>
              <li className="flex items-center gap-3 text-slate-600 font-medium">
                <span className="text-slate-400">✓</span> Up to 3 Risk Analyses
              </li>
              <li className="flex items-center gap-3 text-slate-600 font-medium">
                <span className="text-slate-400">✓</span> Up to 3 Board Reports
              </li>
              <li className="flex items-center gap-3 text-slate-400 line-through">
                <span className="opacity-50">✕</span> Expert Review & Sprint
              </li>
            </ul>
            
            <div className="w-full bg-slate-100 text-slate-500 font-bold py-3 rounded-xl text-center border border-slate-200">
              Active Now
            </div>
          </div>

          {/* Upgrade Tier (Tier 1) */}
          <div className="bg-gradient-to-b from-white to-emerald-50/30 border-2 border-emerald-500 rounded-2xl p-8 shadow-md relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-lg uppercase tracking-wider">Recommended</div>
            
            <div className="inline-block bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 border border-emerald-200">Next Step</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Tier 1 Standard</h3>
            <p className="text-slate-600 text-sm mb-6">Full deployment limits with no expiration date.</p>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-3 text-slate-800 font-bold">
                <span className="text-emerald-500">✅</span> Up to 20 AI Tools
              </li>
              <li className="flex items-center gap-3 text-slate-800 font-bold">
                <span className="text-emerald-500">✅</span> Up to 20 Risk Analyses
              </li>
              <li className="flex items-center gap-3 text-slate-800 font-bold">
                <span className="text-emerald-500">✅</span> Up to 20 Board Reports
              </li>
              <li className="flex items-center gap-3 text-emerald-700 font-bold bg-emerald-50 p-2 rounded-lg border border-emerald-100 -ml-2">
                <span>♾️</span> No 7-Day Expiration Timer
              </li>
            </ul>
            
            <button 
              onClick={handleUpgradeRequest}
              disabled={loading || isImpersonating}
              className={`w-full font-bold py-3 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 ${
                isImpersonating 
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              {isImpersonating ? '🔒 Read-Only (Impersonation)' : loading ? 'Submitting Request...' : '🔓 Request Tier 1 Access'}
            </button>
          </div>

        </div>
      )}
    </div>
  );
}